import { useAppSelector } from "Components/Hooks/hooks";
import Icon, { IconName } from "Components/Icons/Icon";
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

export type UserMenuLabel =
  | "Dashboard"
  | "Quotes"
  | "Invoices"
  | "Clients"
  | "Payments"
  | "Preset Quotes"
  | "Settings";

export type AdminMenuLabel = "Dashboard" | "Users" | "Settings";

export type MenuLabel = UserMenuLabel | AdminMenuLabel;

export interface NavItem {
  id: string;
  label: MenuLabel;
  icon: IconName;
  link: string;
  isOpen: boolean;
  click: (e: React.MouseEvent) => void;
  activePaths: string[];
}

interface NavDataProps {
  iscurrentState: MenuLabel;
  setIscurrentState: (label: MenuLabel) => void;
}

const Navdata: React.FC<NavDataProps> = ({
  iscurrentState,
  setIscurrentState,
}) => {
  const { user } = useAppSelector((state) => state.auth);
  const role = user?.role;
  const [menuState, setMenuState] = useState<Record<MenuLabel, boolean>>({
    Dashboard: false,
    Quotes: false,
    Invoices: false,
    Clients: false,
    Payments: false,
    "Preset Quotes": false,
    Settings: false,
    Users: false,
  });

  const routeMap: any =
    role === "admin"
      ? {
          Dashboard: "/dashboard",
          Users: "/users",
          Settings: "/settings",
        }
      : {
          Dashboard: "/dashboard",
          Quotes: "/quotes",
          Invoices: "/invoices",
          Clients: "/clients",
          Payments: "/payments",
          "Preset Quotes": "/preset-quotes",
          Settings: "/settings",
        };

  useEffect(() => {
    setMenuState((prev: Record<MenuLabel, boolean>) => {
      const updated = { ...prev };
      (Object.keys(updated) as MenuLabel[]).forEach((key) => {
        updated[key] = key === iscurrentState ? prev[key] : false;
      });
      return updated;
    });
  }, [iscurrentState]);

  const toggleMenu = (label: MenuLabel) => {
    setMenuState((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));

    setIscurrentState(label);
  };

  const rawMenuItems: {
    id: string;
    label: MenuLabel;
    icon: IconName;
    activePaths: string[];
  }[] =
    role === "admin"
      ? [
          {
            id: "dashboard",
            label: "Dashboard",
            icon: "Home",
            activePaths: ["dashboard"],
          },
          {
            id: "users",
            label: "Users",
            icon: "ProfileUser",
            activePaths: ["users", "user-detail"],
          },
          {
            id: "settings",
            label: "Settings",
            icon: "Setting",
            activePaths: ["settings", "change-password"],
          },
        ]
      : [
          {
            id: "dashboard",
            label: "Dashboard",
            icon: "Home",
            activePaths: ["dashboard", "notifications-list"],
          },
          {
            id: "quotes",
            label: "Quotes",
            icon: "Receipt",
            activePaths: ["quotes", "quote-details", "manage-quotes"],
          },
          {
            id: "invoices",
            label: "Invoices",
            icon: "Receipt",
            activePaths: ["invoices", "invoice-details", "manage-invoice"],
          },
          {
            id: "clients",
            label: "Clients",
            icon: "ProfileUser",
            activePaths: ["clients", "client-details"],
          },
          {
            id: "payments",
            label: "Payments",
            icon: "CircularEuro",
            activePaths: ["payments", "record-payment", "view-payment"],
          },
          {
            id: "preset-quotes",
            label: "Preset Quotes",
            icon: "ConvertCard",
            activePaths: [
              "preset-quotes",
              "add-preset-quotes",
              "preset-quotes-details",
              "manage-preset-quotes",
            ],
          },
          {
            id: "settings",
            label: "Settings",
            icon: "Setting",
            activePaths: [
              "settings",
              "subscription",
              "profile",
              "payments-and-invoicing",
              "change-password",
              "notifications-preference",
              "team-members",
              "categories",
              "subcategories",
              "items",
              "sections",
            ],
          },
        ];

  const menuItems: NavItem[] = rawMenuItems.map((item) => ({
    ...item,
    link: routeMap[item.label],
    isOpen: menuState[item.label],
    click: (e) => {
      toggleMenu(item.label);
    },
    activePaths: item.activePaths,
  }));

  const location = useLocation();

  return (
    <>
      {menuItems.map((item) => (
        <li className="nav-item" key={item.id}>
          <Link
            to={item.link}
            className="nav-link menu-link"
            onClick={(e) => {
              item.click(e);
            }}
            style={{
              background: item.activePaths.includes(
                (location.pathname ?? "").replace(/^\//, ""),
              )
                ? "linear-gradient(270deg, #082B60 0%, #000000 100%)"
                : "inherit",
              margin: "6px 24px",
              borderRadius: "8px",
              lineHeight: "24px",
              fontSize: "16px",
            }}
          >
            <Icon
              name={
                item.activePaths.includes(
                  (location.pathname ?? "").replace(/^\//, ""),
                )
                  ? ((item.icon + "Active") as IconName)
                  : item.icon
              }
            />
            <span
              style={{
                color: item.activePaths.includes(
                  (location.pathname ?? "").replace(/^\//, ""),
                )
                  ? "white"
                  : "inherit",
              }}
            >
              {item.label}
            </span>
          </Link>
        </li>
      ))}
    </>
  );
};

export default Navdata;
